<template>
  <div class="app-info" v-if="personalInfo">
    <h1>License / ID / Permit</h1>
    <div class="app-row">
      <div>
        <div v-if="personalInfo.credentialInformation">
          <div v-if="personalInfo.credentialInformation.credentialDetails?.length > 0">
            <div
              class="app-cred-row"
              v-for="(credDetails, index) in personalInfo.credentialInformation.credentialDetails"
              :key="index"
            >
              <div class="app-cred-info-row">
                <div class="app-cred-info-group">
                  <label for="licenseType">License type</label>
                  <span for="licenseTypeText">{{ credDetails.licenseType }}</span>
                  <div v-if="(personalInfo.personalInformation.isCitizen && (showRenewalLicenseType(credDetails) || showDuplicateLicenseType(credDetails))) || 
                    (credDetails.licenseType === 'Learner Permit' && credDetails.credentialStatus === 'Active')">
                    <span>
                      <a href="https://www.ri.gov/DMV/licenserenewal" target="_blank" rel="noopener noreferrer" v-if="personalInfo.personalInformation.isCitizen && showRenewalLicenseType(credDetails)">
                        License/State ID/CDL Renewal
                      </a>
                    </span>
                    <span>
                      <a href="https://www.ri.gov/DMV/duplicate_license" target="_blank" rel="noopener noreferrer" v-if="personalInfo.personalInformation.isCitizen && showDuplicateLicenseType(credDetails)">
                        Duplicate Driver’s License 
                      </a>
                    </span>
                    <span>                    
                      <a href="https://www.ri.gov/app/dmv/road_tests/" target="_blank" rel="noopener noreferrer" v-if="credDetails.licenseType === 'Learner Permit' && credDetails.credentialStatus === 'Active'">
                        Road Test Appointments 
                      </a>
                    </span>
                  </div>
                </div>
                <div class="app-cred-info-group">
                  <label for="licenseNumber">License number</label>
                  <span for="licenseNumberText">{{ credDetails.licenseNumber }}</span>
                </div>
                <div class="app-cred-info-group">
                  <label for="issueDate">Issue date</label>
                  <span for="issueDateText">{{ credDetails.issueDate }}</span>
                </div>
                <div class="app-cred-info-group">
                  <label for="expirationDate">Expiration date</label>
                  <span for="expirationDateText">{{ credDetails.expirationDate }}</span>
                </div>
              </div>
              <div class="app-cred-info-row">
                <div class="app-cred-info-group">
                  <label for="status">Status</label>
                  <img
                    v-if="credDetails.credentialStatus == 'Active'"
                    class="active-icon"
                    :src="`/static/status-active.png`"
                    alt="Icon"
                  />
                  <img
                    v-else
                    class="active-icon"
                    :src="`/static/status-not-active.png`"
                    alt="Icon"
                  />
                  <span for="statusText">{{ credDetails.credentialStatus }}</span>
                  <span v-if="credDetails.readyToReinstate == true">
                    <a href="https://www.ri.gov/DMV/license_reinstatement/" target="_blank" rel="noopener noreferrer">Ready to be reinstated</a>
                  </span>
                </div>
                <div class="app-cred-info-group">
                  <label for="realId">Real ID indicator</label>
                  <span for="realIdText">{{ credDetails.realIdIndicator }}</span>
                </div>
                <div class="app-cred-info-group">
                  <label for="endorsements">Endorsements
                    <span v-show="credDetails.endorsements !== 'None'">
                      <Tooltip theme="my-custom-tooltip" :triggers="['hover', 'click', 'focus']">
                        <button type="button" class="btn-tooltip-trigger" aria-label="Endorsement details">
                          <img
                            src="@ridmv/web-themes/images/icons/question-circle-solid.svg"
                            :alt="credDetails.endorsements" 
                          />
                        </button>
                        <span class="sr-only">{{ credDetails.endorsements }}</span>
                        <template #popper>
                          {{ credDetails.endorsements }}
                        </template>
                      </Tooltip>
                    </span>
                  </label>
                  <span
                    :class="{'app-codes': credDetails.endorsements !== 'None'}"
                    for="endorsementsText"
                  >{{ splitCodes(credDetails.endorsements) }}</span>
                </div>
                <div class="app-cred-info-group">
                  <label for="restrictions">Restrictions
                    <span v-show="credDetails.restrictions !== 'None'">
                      <Tooltip theme="my-custom-tooltip" :triggers="['hover', 'click', 'focus']">
                        <button type="button" class="btn-tooltip-trigger" aria-label="Restriction details">
                          <img
                            src="@ridmv/web-themes/images/icons/question-circle-solid.svg"
                            :alt="credDetails.restrictions"
                          />
                        </button>
                        <span class="sr-only">{{ credDetails.restrictions }}</span>
                        <template #popper>
                          {{ credDetails.restrictions }}
                        </template>
                      </Tooltip>
                    </span>
                  </label>
                  <span
                    :class="{'app-codes': credDetails.restrictions !== 'None'}"
                    for="restrictionsText"
                  >{{ splitCodes(credDetails.restrictions) }}</span>
                </div>
              </div>
              <div class="app-cred-info-row" v-if="isMedCertApplicable(credDetails.licenseType)">
                <div class="app-cred-info-group">
                  <label for="licenseType">CDL self certification</label>
                  <span for="licenseTypeText">{{ credDetails.cdlSelfCertificationStatus }}</span>
                </div>
                <div class="app-cred-info-group">
                  <label for="licenseNumber">CDL Med. Cert. status</label>
                  <span for="licenseNumberText">{{ credDetails.medicalCertificateStatus }}</span>
                </div>
                <div class="app-cred-info-group">
                  <label for="issueDate">CDL Med. Cert. issue date</label>
                  <span for="issueDateText">{{ credDetails.medicalCertificateIssueDate }}</span>
                </div>
                <div class="app-cred-info-group">
                  <label for="expirationDate">CDL Med. Cert. exp. date</label>
                  <span for="expirationDateText">{{ credDetails.medicalCertificateExpirationDate }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else><p class="text-mute">No License/ID/Permit found.</p></div>
        </div>
        <div v-if="!personalInfo && !networkResponse.message">
          <p class="text-mute">No License/ID/Permit found.</p>
        </div>
        <div v-if="!personalInfo && networkResponse.message">
          <SystemAlert/>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import * as COMMONS from '../../store/_commons';
import SystemAlert from '@/components/SystemAlert';
import { Tooltip } from 'floating-vue';
import '@/assets/tooltip.css';


// useStore hook to access the Vuex store
const store = useStore();

// Computed properties
const personalInfo = computed(() => store.getters[COMMONS.GUEST_PERSONAL_INFO]);
const networkResponse = computed(() => store.state.networkResponse);
const allowedMedCertLicenseTypes = new Set([
  'CDL Class A',
  'CDL Class B',
  'CDL Class C',
  'CDL Permit Class A',
  'CDL Permit Class B',
  'CDL Permit Class C'
]);

const isMedCertApplicable = (type) => {
  return allowedMedCertLicenseTypes.has(type);
};

// Methods
const splitCodes = (val) => {
  const isEmpty = val === null || val === undefined || val === '' || val === 'None';

  if (isEmpty) {
    return 'None';
  }

  const arrstr = val.split(",");
  let result = '';

  for (let i = 0; i < arrstr.length; i++) {
    const trimmedChar = arrstr[i].trim().charAt(0);
    const addComma = i !== arrstr.length - 1 ? ', ' : '';
    result += trimmedChar + addComma;
  }
  return result;
};

const showDuplicateLicenseType = (cred) => {
  const allowedTypes = [
    'Operator License',
  ];

  const isAllowedType = allowedTypes.includes(cred.licenseType);
  const isActive = cred.credentialStatus === 'Active';

  const isSuspendedFuture = cred.credentialStatus === 'Suspended' && new Date(cred.expirationDate) > new Date();

  return isAllowedType && (isActive || isSuspendedFuture);
}

const showRenewalLicenseType = (cred) => {
  const allowedTypes = [
    'Operator License',
    'State ID',
    'CDL Class A',
    'CDL Class B',
    'CDL Class C'
  ];

  // Must be one of the allowed license types
  if (!allowedTypes.includes(cred.licenseType)) {
    return false;
  }

  const today = new Date();
  const expDate = new Date(cred.expirationDate);

  const daysUntilExpiration = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));

  const isExpired = cred.credentialStatus === 'Expired';

  const isActiveDLorID = cred.credentialStatus === 'Active' && (cred.licenseType === 'Operator License' || cred.licenseType === 'State ID');

  const isActiveCDL = cred.credentialStatus === 'Active' && cred.licenseType.startsWith('CDL Class');

  const activeDLorIDExpiringSoon = isActiveDLorID && daysUntilExpiration <= 90;

  const activeCDLExpiringSoon = isActiveCDL && daysUntilExpiration <= 45;

  return ( isExpired || activeDLorIDExpiringSoon || activeCDLExpiringSoon );
}

</script>


<style scoped lang="scss">
.app-info-row {
  display: flex;
  flex-wrap: wrap;
  margin: 15px 0px 18px 0px;
  border-bottom: 1px solid var(--ri-gray);
  span, label {
      font-size: 1rem;
  }
  .app-info-group {
    width: 28%;
    min-width: 140px;
    margin-bottom: 1.5rem;
    img {
      max-width: 70%;
      max-height: 150px; 
      position: relative;   
      &:focus {
      outline-color: orange;
      }
    }
  }
  .d-flex {
    display: grid;
    grid-template-columns: 180px 180px;
    gap: 1rem 8%;
    margin-left: 5rem;
    .app-info-child-group {
      width: 180px;
    }
  }
}
.app-cred-row {
  border-bottom: 1px solid #F2F2F2;
  padding: 0.8rem 2rem;
}
.app-cred-info-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 5%;
  margin: 15px 0px 10px 0px;
  .app-cred-info-group {
    width: 215px;
    .btn-tooltip-trigger{
      min-width: 25px;
      border: none;
      background-color: unset;
      border-radius: unset;
      padding: 0rem;
    }
    .app-tooltip {
      margin-left: 0.2rem;
      display: inline-block;
    }
    img {
      width: 18px;
      margin-bottom: -2px;
    }
    .active-icon {
      width: 25px;
      height: 25px;
      vertical-align: middle;
      padding-right: 5px;
      padding-bottom: 5px;
    }
    span {
      line-height: 25px;
      display: inline-flex;
    }
    .app-codes {
      position: relative;
      top: -4px;
    }
    :deep(.v-popper--theme-my-custom-tooltip .v-popper__inner) {
      max-width: 250px;
      white-space: normal;
      text-align: left;
      color: red;
    }
    .statusDesc{
      display: flex;
      gap: 0.1rem;
      a {
        font-size: 0.9rem;
        color: var(--ri-blue);
        text-decoration: underline;
        &:hover {
          color: var(--ri-blue-hover);
        }
      }
    }
  }
}
.v-popper--theme-my-custom-tooltip .v-popper__inner {
  max-width: 250px; 
  white-space: normal;
  text-align: left;
  color: red;
}
.app-cred-row:nth-child(odd) {
  background-color: #F2F2F2;
}

.app-row {
  display: grid;
  gap: 1rem;
}

@media only screen and (max-width: 700px) {
  .app-cred-info-row, .app-info-row, .app-row  {
    display: block;
  }
  .app-info-row .d-flex .app-info-child-group {
    width: 100px;
  }
  .app-cred-info-row .app-cred-info-group {
    margin-bottom: 0.7rem;
    .statusDesc{
      display: flex;
      gap: 0.1rem;
      a {
        font-size: 0.5rem;
        color: var(--ri-blue);
        text-decoration: underline;
        &:hover {
          color: var(--ri-blue-hover);
        }
        &:visited{
          font-size: 0.5rem;
        }
      }
    }
  }
  .app-info-row .d-flex {
    margin-left: 0rem;
  }
  .app-cred-info-group{
    ul {
      li {
        width: 100%;
      }
    }
  }
}
@media only screen and (min-width: 600px) and (max-width: 760px) {
  .app-row {
    display: block;
  }
}
 
</style>